// Transforms an object to the FormData

import { ignoreSuppliers } from './config';

export const toFormData = (data) => {
  const formData = new FormData();

  for (let field in data) {
    if (Array.isArray(data[field])) {
      data[field].forEach((d) => {
        formData.append(field, d);
      });
      continue;
    }
    formData.append(field, data[field]);
  }
  return formData;
};

const defArray = Object.freeze([]);

export const getArray = (a) => (Array.isArray(a) ? a : defArray);

export const getFunc =
  (f) =>
  (...args) =>
    typeof f === 'function' && f(...args);

export const getString = (s) => {
  if (typeof s === 'string') {
    return s;
  }
  if (Number.isFinite(s)) {
    return String(s);
  }
  return '';
};

// If you need to emulate loading
export const sleep = async (t = 1500) =>
  new Promise((resolve) => setTimeout(resolve, t));

export const serializeParams = (params = {}) => {
  const result = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    const isEmpty = !value || (Array.isArray(value) && value.length === 0);

    if (isEmpty) {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((v) => {
        result.append(key, v);
      });
    } else {
      result.append(key, value);
    }
  });
  return result;
};

// Regexp object (to keep theme similar for the project)
export const re = {
  username:
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  // At least 8 characters, one uppercase letter, one lowercase letter, one number and one special character
  password:
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$&+_,:;=?@#|'<>.^*()%!-])[A-Za-z\d$&+_,:;=?@#|'<>.^*()%!-]{8,}$/,
  postalCode: /^[A-Z]{2}(-[A-Z0-9]{2})?$/,
  email:
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
};

export const random = (max = 10, min = 0) => {
  const mx = Math.max(max, min);
  const mn = Math.min(max, min);
  return Math.round(Math.random() * (mx - mn)) + mn;
};

// Validators to use with react hook form
export const formValidators = {
  required: 'This field is required',
  statePostalCode: (v) => {
    if (re.postalCode.test(v)) {
      return true;
    }
    return 'Wrong format. Supported format: AA or AA-AA.';
  },
  email: (v) => {
    if (v && !re.email.test(v)) {
      return 'Wrong email.';
    }
    return true;
  },
  stripeField: (v) => {
    return !!v?.complete || 'Wrong value!';
  },
};

export const capitalize = (string) =>
  typeof string !== 'string'
    ? ''
    : `${string[0].toUpperCase()}${string.slice(1).toLowerCase()}`;

export const has = (o, name) =>
  o && typeof o === 'object' && Object.prototype.hasOwnProperty.call(o, name);

export const cropText = (text, limit = 1000) => {
  return text.split(' ').reduce(function (sum, current, index, arr) {
    if (sum.length < limit) return `${sum} ${current}`;
    if (index === arr.length - 1 && text.length > limit) return `${sum}...`;
    return sum;
  });
};

export const getSearchParams = () => {
  const search = (window.location.search || '').slice(1);
  const result = {};

  if (!search) {
    return result;
  }
  const pairs = search.split('&');

  if (!pairs.length) {
    return result;
  }
  pairs.forEach((p) => {
    const key = p.slice(0, p.indexOf('='));
    result[key] = p.slice(p.indexOf('=') + 1);
  });
  return result;
};

export const getStrapiColor = (c) => {
  const color = c?.toLowerCase();

  switch (color) {
    case 'green':
      return '#208C72';
    case 'orange':
      return '#F28520';
    case 'blue':
      return '#3360AD';
    case 'dark blue':
      return '#1E3966';
    case 'yellow':
      return '#FEC910';
    case 'red':
      return '#EA1763';
    case 'violet':
      return '#8614EB';
    case 'grey':
      return '#7E8AA0';
    default:
      return color;
  }
};

export const downloadLink = (src, name) => {
  const link = document.createElement('a');
  link.href = src;
  link.download = name || src;
  link.target = '_blank';
  link.rel = 'noreferrer noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const getResponseMessage = (res) => {
  return getString(res?.data?.detail) || getString(res?.data?.detail?.error);
};

export const snakeCaseToCamelCase = (name, cap) => {
  return getString(name)
    .toLowerCase()
    .split('_')
    .map((part, i) => {
      const first = getString(part[0]);
      const rest = getString(part.slice(1)).toLowerCase();

      return [
        i > 0 || cap ? first.toUpperCase() : first.toLowerCase(),
        rest,
      ].join('');
    })
    .join('');
};

export const getProductLink = (product) => {
  const supplierCode = product?.supplier_code;
  const productSlug = product?.slug || product?.product_slug;
  const brandSlug = product?.brand_slug;

  if (
    [supplierCode, brandSlug, productSlug].some((v) => typeof v !== 'string')
  ) {
    if (typeof productSlug === 'string') {
      return `/product/${productSlug}`;
    }
    return null;
  }
  const [s, b, p] = [supplierCode, brandSlug, productSlug].map((v) =>
    v.toLowerCase(),
  );
  return `/product/${s}/${b}/${p}`;
};

export const getProductPreviewUrl = (url, quality) => {
  const disabled = true;
  const src = getString(url).replaceAll('+', '%2B');
  const path = src.split('/');

  if (path.length < 2 || !quality || disabled) {
    return src;
  }
  path.splice(path.length - 1, 0, quality);
  return path.join('/');
};

export const getHtmlContent = (html) => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return getString(div.textContent);
};

export const isIgnoredSupplier = (sup, ctx) => {
  return ignoreSuppliers.some((ignore) => {
    const inList =
      sup?.id === ignore.id ||
      getString(sup?.title || sup?.name)
        .toLowerCase()
        .includes(ignore.title.toLowerCase());
    const inContext = !ctx || ignore.context.includes(ctx);
    return inList && inContext;
  });
};

export const getSupplierLink = (sup) => {
  const shouldBeIgnored = isIgnoredSupplier(sup, 'links');

  if (shouldBeIgnored) {
    return null;
  }
  const [domain] = getArray(sup?.authorized_domains);

  if (typeof domain !== 'string') {
    return null;
  }
  if (domain.startsWith('http')) {
    return domain;
  }
  return `https://${domain}`;
};

export const getProductConfig = (configurations) => {
  const c = configurations || {};

  return Object.entries(c).reduce((res, [label, [option]]) => {
    return [
      ...res,
      {
        label,
        ...option,
      },
    ];
  }, []);
};

export const getProductTitle = (product, extendedTitle = true) => {
  if (!product) return '';

  const result = [product.title];

  if (extendedTitle && product?.configurations) {
    Object.entries(product.configurations)
      .sort((a, b) => {
        const [nameA] = a;
        const [nameB] = b;

        if (nameA === 'UNIT_SIZE') {
          return -1;
        }
        if (nameB === 'UNIT_SIZE') {
          return 1;
        }
        return nameA.localeCompare(nameB);
      })
      .forEach(([, configs]) => {
        configs.forEach((conf) => {
          if (conf.slug === product.slug) {
            result.push(conf.value);
          }
        });
      });
  }
  return result.join(', ');
};

export const reverseString = (str) =>
  str?.length ? str.split('').reverse().join('') : str;

export const unitPriceFormatter = (value) => {
  const [intValue, tail] = value?.split('.');

  if (intValue?.length < 4) return value;

  const reversed = reverseString(intValue);

  const withComma =
    reversed.substring(0, 3) + ',' + reversed.substring(3, reversed.length);

  return reverseString(withComma) + '.' + tail;
};
